import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listGoogleAccounts, type GoogleAccount } from '../../services/google-account-api.js';
import {
  listIntegrationAccounts,
  upsertIntegrationAccount,
  deleteIntegrationAccount,
  type IntegrationAccount,
} from '../../services/integration-account-api.js';

const REQUIRED_SCOPE = 'https://www.googleapis.com/auth/analytics.readonly';

@customElement('cp-google-analytics-settings')
export class CpGoogleAnalyticsSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _googleAccounts: GoogleAccount[] = [];
  @state() private _integration: IntegrationAccount | null = null;
  @state() private _selectedAccountId = 0;
  @state() private _propertyId = '';
  @state() private _loading = true;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    try {
      const [googleAccounts, integrations] = await Promise.all([
        listGoogleAccounts(),
        listIntegrationAccounts(),
      ]);
      this._googleAccounts = googleAccounts;
      this._integration = integrations.find((i) => i.integration_type === 'google-analytics') ?? null;
      this._selectedAccountId = this._integration?.google_account_id ?? 0;
      this._propertyId = this._integration?.config?.property_id ?? '';
      this._error = '';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _hasRequiredScope(account: GoogleAccount): boolean {
    return account.scopes?.includes(REQUIRED_SCOPE) ?? false;
  }

  private async _handleConnect(): Promise<void> {
    if (!this._selectedAccountId) return;
    try {
      await upsertIntegrationAccount('google-analytics', {
        google_account_id: this._selectedAccountId,
        config: this._propertyId.trim() ? { property_id: this._propertyId.trim() } : {},
      });
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Verbinden';
    }
  }

  private async _handleDisconnect(): Promise<void> {
    if (!confirm('Google Analytics Integration trennen?')) return;
    try {
      await deleteIntegrationAccount('google-analytics');
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Trennen';
    }
  }

  render() {
    if (this._loading) return html`<div class="cp-integration"><p class="cp-integration__empty">Laden...</p></div>`;

    const connectedAccount = this._integration
      ? this._googleAccounts.find((ga) => ga.id === this._integration!.google_account_id)
      : null;

    return html`
      <div class="cp-integration">
        <h3 class="cp-integration__title">Google Analytics</h3>
        <p class="cp-integration__desc">Verbinde einen Google Account fuer Analytics-Daten.</p>

        ${this._error ? html`<p style="color: var(--color-danger, #ff3b30); font-size: var(--font-size-sm);">${this._error}</p>` : nothing}

        ${this._integration && connectedAccount ? html`
          <div class="cp-integration__status">
            <span class="cp-integration__status-dot ${this._hasRequiredScope(connectedAccount) ? 'cp-integration__status-dot--ok' : 'cp-integration__status-dot--warning'}"></span>
            <div class="cp-integration__status-info">
              <div class="cp-integration__status-label">${connectedAccount.account_alias}</div>
              <div class="cp-integration__status-detail">
                ${connectedAccount.google_email}
                ${this._integration.config?.property_id ? html` · Property: ${this._integration.config.property_id}` : nothing}
              </div>
            </div>
            <div>
              ${!this._hasRequiredScope(connectedAccount) ? html`
                <p class="cp-integration__hint">Fehlende Berechtigung — bitte Account unter Google Accounts erneut verbinden.</p>
              ` : nothing}
              <button class="cp-integration__btn cp-integration__btn--danger" @click=${this._handleDisconnect}>Trennen</button>
            </div>
          </div>
        ` : html`
          <div class="cp-integration__form">
            ${this._googleAccounts.length === 0 ? html`
              <p class="cp-integration__hint">Zuerst einen Google Account unter "Google Accounts" verbinden.</p>
            ` : html`
              <div class="cp-integration__field">
                <label class="cp-integration__label">Google Account</label>
                <select class="cp-integration__select"
                  .value=${String(this._selectedAccountId)}
                  @change=${(e: Event) => { this._selectedAccountId = Number((e.target as HTMLSelectElement).value); }}>
                  <option value="0">Account auswaehlen...</option>
                  ${this._googleAccounts.map((ga) => html`
                    <option value=${ga.id}>
                      ${ga.account_alias} (${ga.google_email})
                      ${!this._hasRequiredScope(ga) ? ' — fehlende Scopes' : ''}
                    </option>
                  `)}
                </select>
              </div>
              <div class="cp-integration__field">
                <label class="cp-integration__label">Property ID</label>
                <input class="cp-integration__input"
                  type="text"
                  placeholder="z.B. 529378233"
                  .value=${this._propertyId}
                  @input=${(e: Event) => { this._propertyId = (e.target as HTMLInputElement).value; }}
                />
              </div>
              <div class="cp-integration__actions">
                <button class="cp-integration__btn cp-integration__btn--primary"
                  ?disabled=${!this._selectedAccountId}
                  @click=${this._handleConnect}>Verbinden</button>
              </div>
            `}
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-google-analytics-settings': CpGoogleAnalyticsSettings;
  }
}
